import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { FileItem } from 'ng2-file-upload';

import { Document } from 'app/providers/storage-api/document.service';
import { HighlightLocation } from '../nlp-output.service';
import {
  RealtimeConnection,
  RealtimeEventHandler,
  RealtimeMessage,
  RealtimeService,
} from '../realtime.service';
import { StorageAPIService } from '../storage-api.service';
import { EnableClassTelemetry } from '../../services/otel.service';
import { SystemService } from '../../services/system.service';
import {
  SectionProperties,
  Section as AnalysisSection,
  DocumentAnalysis,
} from './analysis.service';

export type CTDatasetType = 'COLLECTION' | 'FILE';

export interface CTDatasetDescriptor {
  type: CTDatasetType;
  description: string;
  remainTimeToLive: number;
}

export interface CTDatasetNodes {
  nodes: {
    id: string;
    text: string;
  }[];
}

export type CTDatasetRequest = CTFileDataset | CTCollectionDataset;

interface CTFileDataset {
  file: FileItem;
  firstRecordAsHeader?: boolean;
  configId: string;
}

interface CTCollectionDataset {
  collectionName: string;
  projectId?: string;
  configId: string;
}

export interface CTEvaluationMetrics {
  hasComparison: boolean;
  expirationDate: Date;
  expired: boolean;
  docCount: number;
  hits: number;
  added: number;
  removed: number;
  unchanged: number;
  different: number;
  errors: number;
  missed: number;
}

export type CTEvaluationStatus = 'RUNNING' | 'COMPLETED' | 'FAILED';
export type CTEvaluationResultStatus =
  | 'HIT'
  | 'DIDNT_HIT'
  | 'ADDED'
  | 'REMOVED'
  | 'DIFFERENT_HIT'
  | 'UNCHANGED'
  | 'ERROR';

export interface CTEvaluationResponse extends CTEvaluationMetrics {
  queryTestId: string;
  configId: string;
  status: CTEvaluationStatus;
  projectId?: string;
  collectionName?: string;
  creationDate: string;
  error: string;
  results: CTEvaluationResult[];
  hasSections: boolean;
}

export interface CTEvaluationGroupedResponse {
  count: number;
  pageSize: number;
  pageIndex: number;
  items: CTEvaluationGroupedDocument[];
}

export interface CTEvaluationGroupedDocument {
  projectId?: string;
  collectionName?: string;
  documentId: string;
  hasSections: boolean;
  results: CTEvaluationResult[];
}

export interface CTEvaluationResult {
  id: string;
  documentId: string;
  sourceText: string;
  status: CTEvaluationResultStatus;
  semantriaSectionId?: string;
  sectionName?: string;
  sectionProperties?: SectionProperties;
  highlights: CTEvaluationResultHighlights[];
  highlightsBefore?: CTEvaluationResultHighlights[];
  sectionMetadata?: {
    [key: string]: any;
  };
}

export interface CTEvaluationResultHighlights {
  offset: number;
  length: number;
}

export interface CTEvaluationResultExtended extends CTEvaluationResult {
  highlightedSourceText?: string;
  icon?: string;
  color?: string;
}

export type CTEvaluationResultExportFormat = 'csv' | 'xlsx';

export interface CTEvaluationRequest {
  configId: string;
  queryId: string;
  queryName: string;
  query: string;
  channels?: string[];
}

@EnableClassTelemetry()
@Injectable()
export class ConfigurationTesterService extends StorageAPIService {
  constructor(
    private http: HttpClient,
    systemService: SystemService,
    private realtimeService: RealtimeService
  ) {
    super(http, systemService);
  }

  on(namespace: string, fn: RealtimeEventHandler<CTEvaluationResponse>): RealtimeConnection {
    return this.realtimeService.on(
      namespace,
      {
        configurationTestEvaluation: (data: RealtimeMessage<CTEvaluationResponse>) => {
          fn(data);
        },
      },
      'configurationTesterService'
    );
  }

  getDatasetNodes(configId: string): Promise<CTDatasetNodes> {
    return this.get<CTDatasetNodes>('/configuration-tester/dataset/nodes', { configId: configId });
  }

  getDataset(configId: string): Promise<CTDatasetDescriptor> {
    return this.get<CTDatasetDescriptor>('/configuration-tester/dataset', { configId: configId });
  }

  saveDataset(dataset: CTDatasetRequest): Promise<CTDatasetDescriptor> {
    return this.post<CTDatasetDescriptor>(
      '/configuration-tester/dataset',
      this.toFormData(dataset)
    );
  }

  evaluate(configId, item): Promise<CTEvaluationResponse> {
    const payload: CTEvaluationRequest = {
      configId: configId,
      queryId: item.id,
      queryName: item.name,
      query: item.query,
      channels: item.channels ?? [],
    };
    return this.post<CTEvaluationResponse>('/configuration-tester/evaluation/queries', payload);
  }

  getResults(
    queryId: string,
    offset: number,
    limit: number,
    searchText?: string,
    statuses?: string[]
  ): Promise<CTEvaluationResponse> {
    return this.get<CTEvaluationResponse>(
      `/configuration-tester/evaluation/queries/${queryId}/results`,
      {
        offset,
        limit,
        ...(searchText && { searchText }),
        ...(statuses?.length > 0 && { statuses: statuses.join(',') }),
      }
    );
  }

  getGroupedResults(
    queryId: string,
    offset: number,
    limit: number,
    referenceDocumentId?: string,
    searchText?: string,
    statuses?: string[]
  ): Promise<CTEvaluationGroupedResponse> {
    return this.get<CTEvaluationGroupedResponse>(
      `/configuration-tester/evaluation/queries/${queryId}/grouped-results`,
      {
        offset,
        limit,
        ...(referenceDocumentId && { referenceDocumentId }),
        ...(searchText && { searchText }),
        ...(statuses?.length > 0 && { statuses: statuses.join(',') }),
      }
    );
  }

  getResult(queryId: string, documentId: string): Promise<CTEvaluationResponse> {
    return this.get<CTEvaluationResponse>(
      `/configuration-tester/evaluation/queries/${queryId}/results/${documentId}`
    );
  }

  /**
   * Converts a CTEvaluationResponse to a Document.
   * @param response The CTEvaluationResponse to convert.
   * @returns The converted Document.
   */
  convertEvaluationResponseToDocument(response: CTEvaluationGroupedDocument): Document {
    let sectionCharOffset = 0;
    const sectioned: boolean = !!response.hasSections;
    const sectionCreator = (resItem: CTEvaluationResult, idx: number, charOffset: number) =>
      ({
        ...(resItem.sectionProperties ?? {}),
        value: resItem.sourceText,
        sectionId: sectioned ? +resItem.semantriaSectionId : idx,
        charOffset,
        metadata: resItem.sectionMetadata,
      }) as AnalysisSection;

    // Collect sections (non sectioned documents will have only one section)
    const sections: AnalysisSection[] = response.results.map((result, idx) => {
      // Create section
      const section = sectionCreator(result, idx, sectionCharOffset);
      // Increment section char offset
      sectionCharOffset += result.sourceText.length;
      return section;
    });
    const partialAnalysis: Partial<DocumentAnalysis> = {
      flatSections: sections,
    };
    const highlightLocations = this.createHighlightLocations(response);
    const partialDoc: Partial<Document> = {
      id: response.documentId,
      text: response.results.map((result) => result.sourceText).join(' '),
      projectId: response.projectId,
      collectionName: response.collectionName,
      sectioned,
      analyses: [partialAnalysis as DocumentAnalysis],
      highlightLocations,
    };
    return partialDoc as Document;
  }

  /**
   * Creates highlight locations from a CTEvaluationResponse.
   * @param response The CTEvaluationResponse to create highlight locations from.
   * @returns The highlight locations.
   */
  createHighlightLocations(response: CTEvaluationGroupedDocument): HighlightLocation[] {
    const sectioned: boolean = !!response.hasSections;
    let highlightLocationsOffset = 0;
    const highlightLocations: HighlightLocation[] = response.results
      .map((section, idx) => {
        const getFeaturesFromHighlights = (
          highlights: CTEvaluationResultHighlights[],
          color: string,
          priority: number
        ) => {
          return (
            highlights?.map((h) => ({
              title: section.sourceText.slice(h.offset, h.offset + h.length),
              sentimentScore: 0,
              offset: highlightLocationsOffset + h.offset,
              length: h.length,
              section: sectioned ? +section.semantriaSectionId : idx,
              sectionCharOffset: sectioned ? h.offset : undefined,
              color,
              highlightClass: 'static-highlight',
              priority,
            })) ?? []
          );
        };

        const result = [
          // Current highlights with higher priority
          ...getFeaturesFromHighlights(section.highlights, '#0058FF', 120),
          // Previous highlights with lower priority
          ...getFeaturesFromHighlights(section.highlightsBefore, '#929292', 110),
        ];
        // Increment features offset
        highlightLocationsOffset += section.sourceText.length;

        return result;
      })
      .flat();
    return highlightLocations;
  }

  getEvaluationDescriptor(queryId: string): Promise<CTEvaluationResponse> {
    return this.get<CTEvaluationResponse>(`/configuration-tester/evaluation/queries/${queryId}`);
  }

  exportResults(queryId: string, format?: CTEvaluationResultExportFormat): Promise<Blob> {
    return this.getBinary(`/configuration-tester/evaluation/queries/${queryId}/_export`, {
      format,
    });
  }

  extendResultsTime(queryId: string): Promise<CTEvaluationResponse> {
    return this.patch<CTEvaluationResponse>(`/configuration-tester/evaluation/queries/${queryId}`);
  }

  clearResults(options: { configId?: string; queryId?: string }): Promise<void> {
    let url = '/configuration-tester/evaluation/queries';
    if (options.queryId) {
      url += `/${options.queryId}`;
    }
    return this.delete<void>(url, { configId: options.configId });
  }

  private toFormData(dataset: CTDatasetRequest): FormData {
    const fd = new FormData();
    Object.entries(dataset).forEach(([key, value]) => {
      if (value instanceof FileItem) {
        const file = value as FileItem;
        fd.append(key, file._file, value._file.name);
      } else {
        fd.append(key, value);
      }
    });
    return fd;
  }
}
